/**
 * @license
 * Copyright 2024 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import {
  collection,
  getDocsFromServer,
  limit,
  query,
  where,
} from 'firebase/firestore'
import { useParams, usePathname, useRouter } from 'next/navigation'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useFirestore, useUser } from '@aglyn/tenant-feature-instance'
import { useHost } from '../hooks/use-host'
import { hostBrandKey, HostSiteKeyContext } from '@aglyn/shared-ui-theme'
import { useAuthRecovery } from '../hooks/use-auth-recovery'
import { useHostResolution } from '../hooks/use-host-resolution'
import { useOrgScope } from '../hooks/use-org-scope'

/** The resolved host DOC ID — the internal key for all host data reads. */
export const HostIdContext = createContext<string>(null)
/** The host SUBDOMAIN from the URL — used to build `/hosts/[host]` links. */
export const HostSubdomainContext = createContext<string | null>(null)
/**
 * Whether subdomain→doc-id resolution has settled for the current host route
 * (true off host routes). The in-tree HostGuard reads this to hold the spinner
 * until resolution finishes, then 404s an unknown subdomain.
 */
export const HostReadyContext = createContext<boolean>(true)
/**
 * Whether resolution gave up after exhausting its retries (AGL-813). Distinct
 * from "resolved to no match": the HostGuard shows a retry instead of a 404
 * when this is set, so a transient read failure never masquerades as a site
 * that doesn't exist.
 */
export const HostErrorContext = createContext<boolean>(false)
/**
 * Re-runs host resolution (AGL-1200). The guard's "Try again" reloaded the
 * page, which re-entered the same cold-load window and failed identically —
 * so the error state was permanent in practice. This retries in place.
 */
export const HostRetryContext = createContext<() => void>(() => undefined)
/**
 * Whether the failure behind HostErrorContext was the ORG read rather than
 * host resolution (AGL-1260). The guard's copy hangs on this — "your
 * workspaces" failed to load, not this workspace's sites — and the retry it
 * is handed re-runs the membership listen, not subdomain resolution.
 */
export const HostOrgErrorContext = createContext<boolean>(false)
/**
 * Whether the failure behind HostErrorContext was the SESSION rather than
 * the network — a read refused for who you are, not one dropped in transit.
 *
 * Only the copy hangs on this. The RECOVERY does not: an auth-caused failure
 * re-runs itself (see `useAuthRecovery` below and in `OrgScopeProvider`), so
 * by the time a user could read the message the page has usually already
 * healed. What it must never say meanwhile is "check your connection" — the
 * report that started this was a tab whose token had gone stale overnight,
 * told to check a connection that was fine, next to a Try again for a
 * condition Firebase had already resolved on its own.
 */
export const HostAuthErrorContext = createContext<boolean>(false)

/**
 * The current host's per-site plugin deny-list (AGL-1014), [] off host
 * routes or while the doc loads. `useEnabledPluginIds` subtracts it so
 * every host-scoped consumer of the ConsoleExtension registry — nav tabs,
 * plugin pages, widget slots — reads the per-site set from one place.
 */
export const HostDisabledPluginsContext = createContext<readonly string[]>([])
/**
 * The current host's per-site plugin OPT-IN list (AGL-2486): the
 * `defaultOffPerSite` capabilities this site has switched ON, today just
 * `accounts`. [] off host routes or while the doc loads.
 *
 * Separate from the deny-list above because it is read the other way round.
 * A deny-list cannot express "off until asked" — absent means ON there — so
 * without this the console and the besigner would go on reporting a
 * capability as available on every site that had simply never mentioned it,
 * which is exactly how the component drawer kept offering Members blocks for
 * a site whose /signin returns 404.
 */
export const HostEnabledPluginsContext = createContext<readonly string[]>([])
/**
 * Whether the signed-in user is an ADMIN of the current host (AGL-1014):
 * `memberRoles[uid] === 'admin'`, false off host routes or while loading.
 * Gates the host Admin tab and page; the rules enforce the same boundary.
 */
export const HostAdminContext = createContext<boolean>(false)

export const useHostId = () => useContext(HostIdContext)
export const useHostEnabledPlugins = () => useContext(HostEnabledPluginsContext)
export const useHostDisabledPlugins = () =>
  useContext(HostDisabledPluginsContext)
export const useIsHostAdmin = () => useContext(HostAdminContext)
export const useHostSubdomain = () => useContext(HostSubdomainContext)
export const useHostReady = () => useContext(HostReadyContext)
/** Whether host resolution gave up after retries (AGL-813). */
export const useHostError = () => useContext(HostErrorContext)
/** Re-run host resolution after it gave up (AGL-1200). */
export const useHostRetry = () => useContext(HostRetryContext)
/** Whether the ORG read (not host resolution) gave up (AGL-1260). */
export const useHostOrgError = () => useContext(HostOrgErrorContext)
/** Whether the latched failure was the session rather than the network. */
export const useHostAuthError = () => useContext(HostAuthErrorContext)

/**
 * Reads the resolved host's doc and provides its plugin policy (AGL-1014):
 * the per-site `disabledPlugins` deny-list and whether the signed-in user
 * is a site admin. Mounted only once a hostId exists — `useHost` needs a
 * real doc path, and off host routes the contexts' defaults ([] / false)
 * are already the right answer.
 *
 * It is also the one listener open on the host doc for as long as the site is
 * on screen, so it is what notices the site going away underneath the page
 * (AGL-3596): the server saying the doc does not exist, or refusing it — which
 * is how the rules answer for a deleted host, since they read the doc's own
 * `memberRoles`. Either way it reports `onGone`, and the provider asks the
 * server to resolve the subdomain again rather than trusting this listener's
 * verdict alone; a refusal can also be a session fault, and re-resolving is
 * what tells the two apart.
 */
function HostPluginPolicyBridge({
  hostId,
  uid,
  subdomain,
  onGone,
  children,
}: {
  hostId: string
  uid?: string
  subdomain: string | null
  onGone: (hostId: string, missing: boolean) => void
  children: ReactNode
}) {
  const {
    doc: { data: host, status, fromCache, serverDenied },
  } = useHost({ hostId })
  // The server answered and the document is not there: the site is gone, full
  // stop. A refusal is weaker (it can be a session fault), so it is only
  // re-checked.
  const missing = status === 'success' && fromCache === false && !host
  const gone = missing || status === 'error' || Boolean(serverDenied)
  useEffect(() => {
    if (gone) onGone(hostId, missing)
  }, [gone, missing, hostId, onGone])
  const disabledPlugins = useMemo(
    () =>
      Array.isArray(host?.disabledPlugins)
        ? host.disabledPlugins.map(String)
        : [],
    [host?.disabledPlugins],
  )
  const optedInPlugins = useMemo(
    () =>
      Array.isArray(host?.enabledPlugins) ? host.enabledPlugins.map(String) : [],
    [host?.enabledPlugins],
  )
  const isAdmin = Boolean(uid && host?.memberRoles?.[uid] === 'admin')
  /**
   * Which site every themed surface below is showing (AGL-3068).
   *
   * The site's own address, because that is what the published page is
   * served under and what decides the theme base it builds on. Read off the
   * document already open here rather than fetched again, and falling back
   * to the URL's subdomain while it loads — a site with no attached domain
   * is reachable by that subdomain, and the fallback is what keeps the
   * canvas from resolving a base for a nameless site in the meantime.
   */
  const siteKey = hostBrandKey(host) ?? subdomain ?? undefined
  return (
    <HostDisabledPluginsContext.Provider value={disabledPlugins}>
      <HostEnabledPluginsContext.Provider value={optedInPlugins}>
        <HostAdminContext.Provider value={isAdmin}>
          <HostSiteKeyContext.Provider value={siteKey}>
            {children}
          </HostSiteKeyContext.Provider>
        </HostAdminContext.Provider>
      </HostEnabledPluginsContext.Provider>
    </HostDisabledPluginsContext.Provider>
  )
}
HostPluginPolicyBridge.displayName = 'HostPluginPolicyBridge'

export function HostIdProvider({ children }) {
  const params = useParams<{ orgSlug?: string; host?: string }>()
  const hostSubdomain = typeof params?.host === 'string' ? params.host : null
  const firestore = useFirestore()
  const { data: user } = useUser()
  const {
    currentOrg,
    orgs,
    error: orgError,
    authError: orgAuthError,
    retry: orgRetry,
  } = useOrgScope()
  const router = useRouter()
  const pathname = usePathname()

  // Resolve the URL subdomain to a host doc id within the CURRENT org via the
  // user's own `hostMemberships` projection (AGL-844) — two bounded reads with
  // a legacy fallback, replacing the scan of the org's whole host list. Scoping
  // to the current org is what the rules allow; a subdomain owned by another
  // org resolves to nothing here and the cross-org redirect below handles it.
  const {
    hostId: resolvedHostId,
    ready,
    error,
    authError,
    retry,
  } = useHostResolution(
    firestore,
    hostSubdomain,
    user?.uid,
    currentOrg?.$id ?? undefined,
  )
  // A site the server says no longer exists (its document is missing) is not
  // openable, even when the `hostMemberships` projection still maps the
  // subdomain to its id. Resolving from that stale row used to mount the
  // deleted site's pages over a host doc that never arrives: the slug as the
  // site name and a spinner forever. Treat the id as unresolved so the guard
  // shows "This site doesn't exist anymore". Ids are never reused, so the
  // marker never needs clearing; a recreated site has a new id.
  const [missingHostIds, setMissingHostIds] = useState<readonly string[]>([])
  const hostId =
    resolvedHostId && !missingHostIds.includes(resolvedHostId)
      ? resolvedHostId
      : null
  // The org read is resolution's prerequisite: useHostResolution holds while
  // `orgId` is undefined, so when the membership listen died `hostReady`
  // stayed false FOREVER and the guard span indefinitely (AGL-1260). A
  // terminal org failure now SETTLES readiness with the error flag raised —
  // the same "errored is settled, never a false 404" shape resolution itself
  // uses (AGL-813) — and hands the guard the org read's retry instead.
  const orgFailed = Boolean(hostSubdomain) && !currentOrg && orgError
  const hostFailed = Boolean(hostSubdomain) && error
  const hostReady =
    !hostSubdomain || Boolean(currentOrg && ready) || orgFailed

  // A latched error is TERMINAL by construction: the resolution effect's deps
  // are `[firestore, subdomain, uid, orgId, attempt]`, and a refreshed ID
  // token moves none of them — `useUser()` re-emits the same `User` object
  // for the same uid, so React bails out and nothing re-reads. That is fine
  // for the cold-connection failure this retry budget was built for, and
  // wrong for an expired token, which the app is told about explicitly.
  //
  // Only the AUTH half self-heals, and only while it is outstanding. The org
  // side is wired inside `OrgScopeProvider` instead of here, because that
  // state is app-wide and every consumer of it deserves the recovered list,
  // not just this guard.
  useAuthRecovery(hostFailed && authError, retry)
  // Which retry the guard is handed decides which failure it is describing,
  // so the auth flag has to follow the same fork (AGL-1260's shape).
  const authFailed = orgFailed ? orgAuthError : hostFailed && authError

  // The open site went away (AGL-3596): re-resolve from the server, which
  // either lands on the site's current id or settles a miss the guard turns
  // into "this site doesn't exist anymore". Once per host id, so a refusal
  // that re-resolving cannot explain (the subdomain still maps to the same
  // id) is left to the listeners' own recovery instead of looping here.
  const recheckedRef = useRef<string | null>(null)
  const recheckGoneHost = useCallback(
    (goneHostId: string, missing: boolean) => {
      if (missing) {
        setMissingHostIds((ids) =>
          ids.includes(goneHostId) ? ids : [...ids, goneHostId],
        )
        return
      }
      if (recheckedRef.current === goneHostId) return
      recheckedRef.current = goneHostId
      retry()
    },
    [retry],
  )

  // Cross-org deep links (AGL-628). A subdomain belonging to ANOTHER org the
  // user is a member of resolves to nothing above — the org-scoped resolution
  // is all the rules allow — and the guard 404s a site they can actually open.
  //
  // `hostIndex` is signed-in readable and already mirrors `subdomain`, so a
  // single lookup finds the owning org without a new global index. If it is
  // an org the user belongs to, redirect to the canonical URL; if not, leave
  // the 404 standing, because that IS the right answer for a site they
  // cannot open. Either way we never sign anyone out (AGL-623).
  const redirectedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!hostSubdomain || !ready || hostId || !currentOrg) return
    if (redirectedRef.current === hostSubdomain) return
    let active = true
    // From the server, like resolution itself (AGL-3596): a cached index row
    // can outlive the site it names.
    void getDocsFromServer(
      query(
        collection(firestore, 'hostIndex'),
        where('subdomain', '==', hostSubdomain),
        limit(1),
      ),
    )
      .then((snapshot) => {
        if (!active) return
        const owningOrgId = snapshot.docs[0]?.get('orgId') as string | undefined
        if (!owningOrgId || owningOrgId === currentOrg.$id) return
        const target = (orgs ?? []).find((org) => org.$id === owningOrgId)
        if (!target?.slug || !pathname) return
        // Swap only the org segment; the rest of the deep link is still valid.
        const next = pathname.replace(/^\/[^/]+/, `/${target.slug}`)
        redirectedRef.current = hostSubdomain
        router.replace(next)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [
    hostSubdomain,
    ready,
    hostId,
    currentOrg,
    orgs,
    firestore,
    pathname,
    router,
  ])

  return (
    <HostReadyContext.Provider value={hostReady}>
      <HostErrorContext.Provider value={hostFailed || orgFailed}>
        <HostOrgErrorContext.Provider value={orgFailed}>
          <HostAuthErrorContext.Provider value={authFailed}>
            <HostRetryContext.Provider value={orgFailed ? orgRetry : retry}>
              <HostSubdomainContext.Provider value={hostSubdomain}>
                <HostIdContext.Provider value={hostId ?? null}>
                  {hostId ? (
                    <HostPluginPolicyBridge
                      hostId={hostId}
                      uid={user?.uid}
                      subdomain={hostSubdomain}
                      onGone={recheckGoneHost}
                    >
                      {children}
                    </HostPluginPolicyBridge>
                  ) : (
                    children
                  )}
                </HostIdContext.Provider>
              </HostSubdomainContext.Provider>
            </HostRetryContext.Provider>
          </HostAuthErrorContext.Provider>
        </HostOrgErrorContext.Provider>
      </HostErrorContext.Provider>
    </HostReadyContext.Provider>
  )
}
HostIdProvider.displayName = 'HostIdProvider'

export default HostIdProvider
